import React, {useEffect, useRef, useState} from "react";
import logo from "../img/official_logo.svg";
import SearchBox from "../components/SearchBox";
import { inject, observer } from "mobx-react";

import {setPageTitle} from "../lib/utils";
import ChatSession from "../components/ChatSession";
import {DEV, getConfig} from "../lib/configMgr";
import { AGENT_COACH, AGENT_GUIDE, AGENT_HELP, useLLMClient } from "../llm/LLMClient";
import { resolveSessionAgent } from "../llm/agentGating";
import useCoachEntitled from "../llm/useCoachEntitled";

const {withAutoplex, defaultToChat = false, loadInitialConversation = false} = getConfig()

const LandingPage = ({
                       searchSessionStore: {
                         resolveSourceLinks,
                       },
                       appStatusStore,
                       userStore,
                       entitlementsStore,
                       tutorialStore,
                       showChat = defaultToChat,
                       toggleShowChat = () => {},
                       onConversationStateChange = () => {},
                       ...chatParams
                     }) => {
  const coachEntitled = useCoachEntitled(userStore, entitlementsStore);
  const sessionAgent = resolveSessionAgent({
    withAutoplex,
    coachEntitled,
  });
  
  const client = useLLMClient(sessionAgent, null, userStore?.authData, userStore, appStatusStore);

  const conversationActive = client.turns.length > 0;
  const {aiModels = {}} = appStatusStore;
  const aiAvailable = Object.keys(aiModels || {}).length > 0;
  if (!aiAvailable) {
    console.warn("No working LLM configuration found");
  }
  else if (DEV) {
    //console.log("AI models", aiModels);
  }

  // clearSearch() is called by App.js navigation effect when isLandingPage becomes true,
  // ensuring it runs synchronously before render (avoiding the flash of stale search tokens)
  useEffect(() => {
    setPageTitle(showChat ? "Plex AI Chat" : "Plex Search");
  }, [showChat]);

  const [downloadContext, setDownloadContext] = useState(null);

  // Notify parent about conversation state changes
  useEffect(() => {
    const currentId = downloadContext?.conversationId || null;
    onConversationStateChange({
      conversationActive,
      client,
      showChat,
      downloadContext,
      sessionAgent,
      currentConversationId: currentId,
      onNewConversation: () => {
        // Use the proper discard function from ChatSession
        // This clears conversationIdRef and prevents save attempts on deleted conversations
        if (downloadContext?.onNewConversation) {
          downloadContext.onNewConversation();
        } else {
          // Fallback if callback not available
          client.clear();
        }
      },
      onClearConversation: () => {
        // Clear current conversation (e.g., when deleting from history)
        if (downloadContext?.onClearConversation) {
          downloadContext.onClearConversation();
        } else {
          // Fallback if callback not available
          client.clear();
        }
      },
      onLoadConversation: (conversation) => {
        if (downloadContext?.restoreConversation) {
          return downloadContext.restoreConversation(conversation);
        }
        return false;
      }
    });
  }, [conversationActive, client, showChat, downloadContext, sessionAgent, onConversationStateChange]);

  // Exit landing tutorial if user navigates away (e.g. submits search manually)
  useEffect(() => {
    return () => {
      if (tutorialStore.isActive && tutorialStore.currentContext === 'landing') {
        tutorialStore.exitTutorial();
      }
    };
  }, [tutorialStore]);

  useEffect(() => {
    // Tutorial auto-trigger disabled (feature in progress)
    return;
  }, [tutorialStore, userStore, showChat]);

  const isIFrame = window.self !== window.top;

  return (
    <div className={`app-container${isIFrame || (showChat && conversationActive) ? " shrink" : ""}`}>
      <div className="logo-wrapper" title={showChat ? "Run a focused Plex search" : "Chat with Plexy, the Plex AI"}>
        <img tabIndex={0} src={logo}
             onClick={() => toggleShowChat()}
             onKeyDown={(e) => (e.key === ' ' || e.key === 'Enter') && toggleShowChat()}
             className={`app-logo`}
             alt="Plex Research"
             />
      </div>
      <div key="landing-page-inputs" className={`landing-page-inputs`}>
        <div className="landing-page-inputs-content">
          {showChat && aiAvailable ? (
            <ChatSession
              key="chat-session"
              autoFocus={true}
              client={client}
              userStore={userStore}
              showSettings={false}
              appStatusStore={appStatusStore}
              useTools={withAutoplex}
              sessionAgent={sessionAgent}
              allowAttachments={withAutoplex}
              showExports={true}
              resolveSourceLinks={resolveSourceLinks}
              loadInitialConversation={loadInitialConversation}
              onDownloadContextChange={setDownloadContext}
              {...chatParams}
            />
          ) : (
            <SearchBox key="search-box" withAutoplex={withAutoplex} aiAvailable={aiAvailable} hideAIButton={true} />
          )}
        </div>
      </div>
    </div>
  );
}

export default inject("searchSessionStore", "appStatusStore", "userStore", "entitlementsStore", "tutorialStore")(observer(LandingPage));
